/**
 * LegacyFileService - Handles legacy S3-only file operations
 * 
 * This service provides backward compatibility for files that haven't been
 * migrated to the new DynamoDB + S3 architecture. It uses AWS Amplify Storage
 * directly against S3.
 * 
 * @module LegacyFileService
 */

import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';

/**
 * Check if a file exists in S3 by its key
 * @param {string} key - S3 key to check
 * @returns {Promise<boolean>} True if file exists
 */
export const exists = async (key) => {
  try {
    await getUrl({
      key,
      options: { validateObjectExistence: true }
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Download a legacy file from S3
 * @param {string} key - S3 key of the file
 * @returns {Promise<void>}
 */
export const download = async (key) => {
  if (!key) {
    throw new Error('Key is required');
  }

  const fileName = key.split('/').pop();

  const result = await getUrl({
    key,
    options: {
      download: true,
      validateObjectExistence: false,
      expiresIn: 300
    }
  });

  // Trigger browser download
  const a = document.createElement('a');
  a.href = result.url.toString();
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 100);
};

/**
 * Delete a legacy file from S3
 * @param {string} key - S3 key of the file
 * @returns {Promise<void>}
 */
export const deleteFile = async (key) => {
  if (!key) {
    throw new Error('Key is required');
  }
  await remove({ key });
};

/**
 * List all files under a prefix (legacy folder listing)
 * @param {string} prefix - S3 prefix to list
 * @returns {Promise<Array>} Array of file objects
 */
export const listByPrefix = async (prefix) => {
  const normalizedPrefix = prefix ? (prefix.endsWith('/') ? prefix : `${prefix}/`) : '';
  
  const response = await list({
    prefix: normalizedPrefix
  });

  return response.items || [];
};

/**
 * Get file metadata from S3 (size, lastModified, etc.)
 * @param {string} key - S3 key
 * @returns {Promise<Object>} File metadata
 */
export const getMetadata = async (key) => {
  const response = await list({
    prefix: key
  });

  const item = response.items?.find(i => i.key === key);
  if (!item) {
    throw new Error('File not found');
  }

  return {
    key: item.key,
    name: key.split('/').pop(),
    size: item.size || 0,
    lastModified: item.lastModified ? new Date(item.lastModified) : new Date()
  };
};

const LegacyFileService = {
  exists,
  download,
  delete: deleteFile,
  listByPrefix,
  getMetadata
};

export default LegacyFileService;
